import { Alert, Grid } from '@mui/material';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Button } from '@efexteam/components';
import { uploadIcon } from '../../../assets';
import useMobileAgent from '../../../hooks/useMobileAgent';
import FileDetail from '../FileDetail';

interface UploadFileInputProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  files?: File[];
  onDelete: (files: File[]) => void;
  onPreventUpload?: (event: boolean) => void;
  fullWidth?: boolean;
  title?: string;
  subTitle?: string;
  classes?: string;
  showDesktopMessage?: boolean;
  inputName?: string;
}

const UploadFileInput = ({
  files,
  onChange,
  onPreventUpload,
  onDelete,
  fullWidth = false,
  inputName = 'invoice',
  title,
  subTitle,
  classes,
  showDesktopMessage = false,
}: UploadFileInputProps) => {
  const [isMobile, isAndroid] = useMobileAgent();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentFiles = useMemo(() => files, [files]);
  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  const handleDeleteFile = (name: string) => {
    const newFiles = currentFiles?.filter((file) => file.name !== name);
    if (fileInputRef.current?.files) {
      fileInputRef.current.value = '';
    }
    onPreventUpload?.(false);
    onDelete(newFiles ?? []);
  };

  return (
    <div className="w-full text-center my-5">
      {isMobile && isAndroid && (
        <Alert severity="success">{t('androidDirections')}</Alert>
      )}
      {!isMobile && showDesktopMessage && (
        <Alert severity="success">{t('selectMultipleFiles')}</Alert>
      )}
      <Button
        id={title}
        type="button"
        appearance="primary"
        variant="outlined"
        className={clsx('mb-1 justify-center items-center mx-auto', classes)}
        endIcon={
          <img className="w-[15px] mr-1" src={uploadIcon} alt="upload" />
        }
        onClick={handleClick}
      >
        <p className="text-left">
          <span>{title ?? t('uploadInvoice')}</span>
          <br />
          {subTitle && <span className="text-xs">{subTitle}</span>}
        </p>
      </Button>
      <input
        id={title?.split(' ').join('_')}
        aria-label="upload file"
        ref={fileInputRef}
        type="file"
        name={inputName}
        multiple
        onChange={onChange}
        hidden
      />
      <Grid
        container
        spacing={2}
        className="max-h-[140px] overflow-hidden overflow-y-auto"
      >
        {currentFiles &&
          currentFiles.map((file) =>
            file && file.name ? (
              <Grid xs={12} lg={fullWidth ? 12 : 6} item key={file.name}>
                <FileDetail
                  sizeLimit={onPreventUpload}
                  onDelete={handleDeleteFile}
                  file={file}
                />
              </Grid>
            ) : null
          )}
      </Grid>
    </div>
  );
};

export default UploadFileInput;
